import {branch as branchHOC, renderComponent, withProps} from 'recompose'

import {skipToken} from '@reduxjs/toolkit/query'
import compose from 'lodash/flowRight'
import {memo} from 'react'
import {connect} from 'react-redux'
import type {EntryPointProps} from 'react-relay'
import {usePreloadedQuery} from 'react-relay'

import {EntityRedirect} from '../../../containers/EntityLink'
import withEffect from '../../../hocs/withEffect'
import withHook from '../../../hocs/withHook'
import {withBranchProps, withProjectId} from '../../../hooks/routes'
import type {State} from '../../../reducers/types'
import {getSingleProjectArg} from '../../../rest/projects'
import Routes, {getHref} from '../../../routes'
import {isProjectLoaded} from '../../../selectors'
import {restApi} from '../../../services/rest'
import type {ActiveEntityProps} from '../../../types'
import {ROOT_PROJECT_INTERNAL_ID} from '../../../types'
import {BS} from '../../../types/BS_types'
import {stringifyBranch} from '../../../utils/branchNames'
import {extractError} from '../../../utils/extractError'
import type {BranchSelectBranchesQuery} from '../../common/BranchSelect/__generated__/BranchSelectBranchesQuery.graphql'
import Redirect from '../../common/Redirect/Redirect'
import {pipelinesApi} from '../PipelinesPages/services/pipelinesApi'
import {isPipelinesEnabled} from '../PipelinesPages/utils/featureToggles'

import type {ProjectPageQuery} from './__generated__/ProjectPageQuery.graphql'
import type {ProjectChangeLogTabEntryPoint} from './ProjectChangeLogTab/ProjectChangeLogTab.entryPoint'
import type {ProjectInvestigationsTabEntryPoint} from './ProjectInvestigationsTab/ProjectInvestigationsTab.entryPoint'
import ProjectPage from './ProjectPage'
import {projectPageQuery} from './ProjectPage.queries'
import type {Props, ReduxOwnProps, StateProps} from './ProjectPage.types'

const mapStateToProps = (state: State, {projectId}: ReduxOwnProps): StateProps => ({
  projectLoaded: isProjectLoaded(state, projectId),
})

// compose is only well typed for 2-7 arguments
const ProjectPageContainer = compose(
  compose(
    withProjectId,
    memo,
    withProps(({projectId}: ActiveEntityProps) => ({
      key: projectId,
    })),
    withBranchProps,
  ),
  compose(
    withEffect(
      (props: ReduxOwnProps) => props.projectId,
      props => props.branch,
      (projectId, branch) => {
        BS?.Branch?.registerProjectBranch(
          projectId,
          branch !== undefined ? stringifyBranch(branch) || BS?.Branch?.WILDCARD_NAME : null,
        )
      },
    ),
    connect(mapStateToProps),
    withHook(({projectId}: ReduxOwnProps) => {
      const {actualId, error, refetch} = restApi.endpoints.getProjectNormalized.useQuery(
        getSingleProjectArg(projectId),
        {selectFromResult: ({data, error}) => ({actualId: data?.result, error})},
      )
      if (error != null) {
        throw extractError(error)
      }
      return {
        actualId,
        idChanged: actualId != null && actualId !== projectId,
        fetchProject: refetch,
      }
    }),
    branchHOC(
      (props: Props) => props.idChanged,
      renderComponent(({projectId, actualId}: Props) => (
        <EntityRedirect projectId={actualId} redirectedFromId={projectId} />
      )),
    ),
  ),
)(ProjectPage)

export type Queries = {main: ProjectPageQuery; branches?: BranchSelectBranchesQuery}
export type NestedEntryPoints = {
  investigationsTab?: typeof ProjectInvestigationsTabEntryPoint
  changeLogTab?: typeof ProjectChangeLogTabEntryPoint
}

export default function ProjectPageOuterContainer({
  queries,
  entryPoints,
}: EntryPointProps<Queries, NestedEntryPoints, {}, {}>) {
  const {project} = usePreloadedQuery(projectPageQuery, queries.main)
  const {internalId} = project ?? {}
  const {isPipeline} = pipelinesApi.endpoints.getPipelineById.useQuery(
    (isPipelinesEnabled && internalId !== ROOT_PROJECT_INTERNAL_ID && internalId) || skipToken,
    {selectFromResult: ({data}) => ({isPipeline: data != null})},
  )

  return internalId != null && isPipeline ? (
    <Redirect to={getHref(Routes.PIPELINE, {pipelineId: internalId})} />
  ) : (
    <ProjectPageContainer
      projectKey={project}
      entryPoints={entryPoints}
      branchesQueryRef={queries.branches}
    />
  )
}
export type ProjectPageOuterContainerComponent = typeof ProjectPageOuterContainer
